import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import {
    CashMovementActivityTypeFilter,
    CashMovementStatusType,
    RequestStatusType,
    RequestType
} from '../constants/request';
import { Range } from 'react-date-range';
import { Option } from 'neon-design-system';
import { subDays } from 'date-fns';
import { TabValue } from '../types/request';
import { ALL_PLATFORMS } from '../constants/platform';

const defaultFilters = {
    requestType: RequestType.MY_REQUESTS,
    requestStatus: RequestStatusType.ALL,
    ranges: [
        {
            startDate: subDays(new Date(), 7),
            endDate: new Date(),
            key: 'selection'
        }
    ],
    selectedAccounts: null,
    tab: 'latest' as TabValue
};

const defaultRiskLimitsFilters = {
    ...defaultFilters,
    platform: ALL_PLATFORMS
};
const defaultManageLimitsFilters = {
    selectedAccounts: null
};

const defaultCashMovementFilters = {
    requestType: RequestType.MY_REQUESTS,
    ranges: [
        {
            startDate: subDays(new Date(), 7),
            endDate: new Date(),
            key: 'selection'
        }
    ],
    selectedAccounts: null,
    tab: 'latest' as TabValue,
    requestStatus: CashMovementStatusType.ALL,
    activityType: CashMovementActivityTypeFilter.ALL
};

const defaultContext = {
    filters: {
        accountChangeRequests: defaultFilters,
        riskLimitsRequests: defaultRiskLimitsFilters,
        manageLimits: defaultManageLimitsFilters,
        cashMovementRequests: defaultCashMovementFilters
    } as SelectedFilters,
    setFilters: () => {}
};

export interface ManageLimitsFilters {
    selectedAccounts: Option[] | null;
    requestType?: RequestType;
    ranges?: Range[];
    requestStatus?: RequestStatusType;
}

export interface DefaultFilters extends ManageLimitsFilters {
    requestType: RequestType;
    requestStatus: RequestStatusType;
    ranges: Range[];
    tab: TabValue;
}

export interface CashMovementRequestFilters {
    requestType: RequestType;
    ranges: Range[];
    tab: TabValue;
    selectedAccounts: Option[] | null;
    requestStatus?: CashMovementStatusType;
    activityType: CashMovementActivityTypeFilter;
}

export interface RiskLimitsFilters extends DefaultFilters {
    platform: string;
}

export type FilterKey = keyof SelectedFilters | undefined;

export interface SelectedFilters {
    accountChangeRequests: DefaultFilters;
    riskLimitsRequests: RiskLimitsFilters;
    manageLimits: ManageLimitsFilters;
    cashMovementRequests: CashMovementRequestFilters;
}

export interface FiltersContext {
    filters: SelectedFilters;
    setFilters: (filters: SelectedFilters) => void;
}

const context = createContext<FiltersContext>(defaultContext);

export const useFilters = () => useContext(context);

interface FiltersProviderProps {
    children: ReactNode;
}

export const FiltersProvider = ({ children }: FiltersProviderProps) => {
    const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(defaultContext.filters);

    useEffect(() => {
        const filtersFromStorage = sessionStorage.getItem('filters');
        const filtersParsed: SelectedFilters | null =
            filtersFromStorage && JSON.parse(filtersFromStorage);

        if (filtersParsed) {
            setSelectedFilters((f) => ({
                ...f,
                ...filtersParsed
            }));
        }
    }, []);

    useEffect(() => {
        sessionStorage.setItem('filters', JSON.stringify(selectedFilters));
    }, [selectedFilters]);

    return (
        <context.Provider
            value={{
                filters: selectedFilters,
                setFilters: setSelectedFilters
            }}
        >
            {children}
        </context.Provider>
    );
};
